import { Box, useTheme } from "@mui/material";
import { BindDialogReturn, DialogProps, DialogResult, DialogState, bindDialog, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import FullScreenDialog, { FullScreenDialogCloseReason } from "../full-screen-dialog/FullScreenDialog";
import SettingsBar, { SettingsActionEnum } from "../settings-bar/SettingsBar";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import AlarmSetpointTestDisableUtils, { AlarmSetpointTestDisableFormValuesType, defaultFormValues } from "./AlarmSetpointTestDisableUtils";
import { useForm } from "react-hook-form";
import { IEditDTO, ISummarySetPointDTO, ISummaryUsageDTO } from "../../services/setpoint-administration-service/Models";
import { useTranslation } from "react-i18next";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useSetpointUsageData } from "../../queries/alarm-setpoint-admin/UseSetpointUsageData";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import Form from "../forms/form/Form";
import TabBar from "../tab-bar/TabBar";
import { AlarmSetpointTestDisableTabEnum, useAlarmSetpointTestDisableState } from "./AlarmSetpointTestDisableState";
import TabContainer from "../tab-container/TabContainer";
import AlarmSetpointTestDisableList from "./AlarmSetpointTestDisableList";
import TabWithCount from "../tab-with-count/TabWithCount";
import { getObjectTypeName } from "../../common/utilities/GetObjectTypeName";
import { useEffect, useState, type JSX } from "react";
import { useGetSetpoint } from "../../queries/alarm-setpoint-admin/UseGetSetpoint";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useGroup } from "../../queries/groups/UseGroup";
import { ContextActionMenu } from "../contextual-app-bar/ContextActionMenu";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import _ from "lodash";
import { useSaveSetpoint } from "../../queries/alarm-setpoint-admin/UseSaveSetpoint";

export type AlarmSetpointTestOrDisableDialogInitValues = {
     groupID: number;
     setpoint: ISummarySetPointDTO
};

export type AlarmSetpointTestOrDisableDialogResultValues = {
    setpointID: number | undefined;
};

export type AlarmSetpointTestOrDisableDialogResultReason = FullScreenDialogCloseReason | "cancel" | "save";

export type AlarmSetpointTestOrDisableDialogResult = DialogResult<AlarmSetpointTestOrDisableDialogResultValues, AlarmSetpointTestOrDisableDialogResultReason>;

export type AlarmSetpointTestOrDisableDialogProps = DialogProps<AlarmSetpointTestOrDisableDialogInitValues, AlarmSetpointTestOrDisableDialogResult>;

export default function AlarmSetpointTestOrDisableDialog({
    open,
    onClose,
    values
}: AlarmSetpointTestOrDisableDialogProps): JSX.Element {
    
    const [openSideSheet, setOpenSideSheet] = useState(false);
    const [viewOptionState, setViewOptionState] = useState(ViewOptionStateEnum.Default);
    const { confirm, acknowledge } = useConfirmation();
    const [userState] = useUserContext();
    const { t } = useTranslation();
    const theme = useTheme();

    const {state,
        setActiveTab,
    } = useAlarmSetpointTestDisableState();

    const {data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID: values?.groupID as number,
        enabled: values !== undefined
    });

    const {data: usageData, refetch: refetchUsageData } = useQueryNotifications(useSetpointUsageData)({
        groupID: values?.groupID as number,
        setpointID: values?.setpoint.id as number,
        enabled: values?.groupID !== undefined
    });

    const {data, refetch } = useQueryNotifications(useGetSetpoint)({
        groupID: values?.groupID as number,
        organizationID: group?.organizationID as number,
        setpointID: values?.setpoint.id as number,
        enabled: group !== undefined
    });

    const { mutate: saveSetpoint/*, status*/ } = useQueryNotifications(useSaveSetpoint)({
        groupID: values?.groupID as number,
        organizationID: group?.organizationID as number,
        onSuccess: async (data) => {
            acknowledge(t("setpointAdmin.successMessageUpdated", { 
                                name: data.setPointName 
                        }));

            refetch();

            setActiveTab(AlarmSetpointTestDisableTabEnum.None);

            onClose({
                reason: "save"
            });
        }
    });

    const form = useForm<AlarmSetpointTestDisableFormValuesType>({
        mode: "onChange",
        defaultValues: {...defaultFormValues
        }
    });

    const {
        //control,
        //setValue,
        //getValues,
        handleSubmit,
        formState: {
            isDirty,
            isValid,
            isSubmitting,
            //isSubmitted
            //errors
            //dirtyFields
        },
        reset,
        watch,
       //clearErrors,
        //trigger
    } = form;

    const [
        devices, 
         wells, 
         tanks,
         tankBatteries
         ] = watch([
         "devices", 
         "wells", 
         'tanks',
         'tankBatteries'
    ]);

    const deviceCount = devices.length;
    const wellCount = wells.length;
    const tankCount = tanks.length;
    const tankBatteryCount = tankBatteries.length;

    const count = deviceCount + wellCount + tankCount + tankBatteryCount;

    async function onOkay(formValues: AlarmSetpointTestDisableFormValuesType) {

        const setpoint = _.cloneDeep(data) as IEditDTO;

        setpoint?.tags.forEach(o => {
            let usageItem: ISummaryUsageDTO | undefined;

            switch (o.tagObjectTypeID) {
                case eLynxObjectTypeEnum.Device:
                    usageItem = formValues.devices.find(v => v.tagID === o.tagID);
                    break;

                case eLynxObjectTypeEnum.Well:
                    usageItem = formValues.wells.find(v => v.tagID === o.tagID);
                    break;

                case eLynxObjectTypeEnum.Tank:
                    usageItem = formValues.tanks.find(v => v.tagID === o.tagID);
                    break;

                case eLynxObjectTypeEnum.TankBattery:
                    usageItem = formValues.tankBatteries.find(v => v.tagID === o.tagID);
                    break;

                default:
                    console.log(`disable setpoint dialog invalid object type: ${o.tagObjectTypeID}`)
            }
            
            if (usageItem) {
                o.notifyOnAlarm = usageItem.notifyOnAlarm;
                o.notifyOnNormal = usageItem.notifyOnNormal;
            }
        });

        saveSetpoint(setpoint);
    }

    async function onCancel() {

        const canCancel = isDirty
            ? await getUserConfirmation()
            : true;

        if (canCancel) {
            reset({...defaultFormValues});
            setActiveTab(AlarmSetpointTestDisableTabEnum.None);
            onClose && onClose({ reason: "cancel" });
        }
    }

    async function getUserConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    function onDisabled() {
        refetchUsageData();
    }

    useEffect(() => {

        if (open && usageData && data) {
            
            const formData = AlarmSetpointTestDisableUtils.mapToFormValues(data, usageData);
            reset(formData);
        }
    }, [
        open,
        usageData,
        data,
        reset
    ]);

    useEffect(() => {


        if (open && count > 0 && state.activeTab === AlarmSetpointTestDisableTabEnum.None) {

            let activeTab = AlarmSetpointTestDisableTabEnum.Devices;
            if (deviceCount === 0) {
                if (wellCount > 0) {
                    activeTab = AlarmSetpointTestDisableTabEnum.Wells;
                } else if (tankCount > 0) {
                    activeTab = AlarmSetpointTestDisableTabEnum.Tanks;
                } else {
                    activeTab = AlarmSetpointTestDisableTabEnum.TankBatteries;
                }
            }
            setActiveTab(activeTab);
        }
    }, [
        open,
        count,
        state.activeTab,
        deviceCount,
        wellCount,
        tankCount,
        tankBatteryCount,
        setActiveTab
    ])


    //console.log(`isDirty: ${isDirty} ${JSON.stringify(dirtyFields)}`);

    return (values !== undefined && state.activeTab !== -1 ? <FullScreenDialog open={open} onClose={reason => onClose({ reason: reason })}>

        <SettingsBar title={values?.setpoint.name} 
                    onClose={
                        async (reason: SettingsActionEnum) => {
                            reason === SettingsActionEnum.OK
                                ? handleSubmit(async (data) => onOkay(data))()
                                : onCancel();
                        }}
                    modified={isDirty && isValid && !isSubmitting} />

        <Form form={form} sx={{ padding: 0 }}>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1 1 auto',
                    overflow: 'hidden',
                    position: 'relative'
                }}>

                <TabBar  value={state.activeTab}
                    onChange={(_event, value) => setActiveTab(value)}
                >
                    {deviceCount > 0 && 
                        <TabWithCount label={getObjectTypeName(eLynxObjectTypeEnum.Device, true)} 
                        count={deviceCount}
                        value={AlarmSetpointTestDisableTabEnum.Devices}
                    />
                    }

                    {wellCount > 0 &&  
                    <TabWithCount label={getObjectTypeName(eLynxObjectTypeEnum.Well, true)} 
                        count={wellCount}
                        value={AlarmSetpointTestDisableTabEnum.Wells}
                    />
                    }

                    {tankCount > 0 && 
                    <TabWithCount label={getObjectTypeName(eLynxObjectTypeEnum.Tank, true)} 
                        count={tankCount}
                        value={AlarmSetpointTestDisableTabEnum.Tanks}
                    />
                    }

                    {tankBatteryCount > 0 && 
                    <TabWithCount label={getObjectTypeName(eLynxObjectTypeEnum.TankBattery, true)} 
                        count={tankBatteryCount}
                        value={AlarmSetpointTestDisableTabEnum.TankBatteries}
                    />
                    }

                </TabBar>

                
                
                    {/* // NOTE this has to position absolute, othwerwise the Tab component throws errors */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: theme.spacing(3)
                        }}>
                    <ContextActionMenu 
                        //actions={actions}
                        viewOptionState={viewOptionState}
                        //onExecuteAction={onExecuteAction}
                        onOverrideViewOptionsClick={() => {setOpenSideSheet(!openSideSheet)}}
                        buttonColor="default"
                        hideAddToFavoritesAction={true}
                        />
                    </Box>



                <TabContainer visible={state.activeTab === AlarmSetpointTestDisableTabEnum.Devices}>
                    
                    <AlarmSetpointTestDisableList 
                        groupID={values?.groupID as number}
                        setpointName={values?.setpoint.name}
                        name='devices'
                        alarmColor={values?.setpoint.alarmColor ?? ''}
                        acknowledgeColor={values?.setpoint.acknowledgeColor ?? ''} 
                        openSideSheet={openSideSheet} 
                        onCloseSideSheet={() => setOpenSideSheet(false)}
                        viewOptionState={viewOptionState}
                        setViewOptionState={setViewOptionState} 
                        visible={state.activeTab === AlarmSetpointTestDisableTabEnum.Devices ? true : false}
                        onDisabled={onDisabled}
                    />
                </TabContainer>


                <TabContainer visible={state.activeTab === AlarmSetpointTestDisableTabEnum.Wells}>
                    <AlarmSetpointTestDisableList 
                        groupID={values?.groupID as number}                        
                        setpointName={values?.setpoint.name}
                        name='wells'
                        alarmColor={values?.setpoint.alarmColor ?? ''}
                        acknowledgeColor={values?.setpoint.acknowledgeColor ?? ''} 
                        openSideSheet={openSideSheet} 
                        onCloseSideSheet={() => setOpenSideSheet(false)}
                        viewOptionState={viewOptionState}
                        setViewOptionState={setViewOptionState} 
                        visible={state.activeTab === AlarmSetpointTestDisableTabEnum.Wells ? true : false}
                        onDisabled={onDisabled}
                    />
                </TabContainer>

                <TabContainer visible={state.activeTab === AlarmSetpointTestDisableTabEnum.Tanks}>
                    <AlarmSetpointTestDisableList 
                        groupID={values?.groupID as number}                        
                        setpointName={values?.setpoint.name}
                        name='tanks'
                        alarmColor={values?.setpoint.alarmColor ?? ''}
                        acknowledgeColor={values?.setpoint.acknowledgeColor ?? ''}
                        openSideSheet={openSideSheet} 
                        onCloseSideSheet={() => setOpenSideSheet(false)}
                        viewOptionState={viewOptionState}
                        setViewOptionState={setViewOptionState} 
                        visible={state.activeTab === AlarmSetpointTestDisableTabEnum.Tanks ? true : false}
                        onDisabled={onDisabled}
                    />
                </TabContainer>

                <TabContainer visible={state.activeTab === AlarmSetpointTestDisableTabEnum.TankBatteries}>
                    <AlarmSetpointTestDisableList 
                        groupID={values?.groupID as number}
                        setpointName={values?.setpoint.name}
                        name='tankBatteries'
                        alarmColor={values?.setpoint.alarmColor ?? ''}
                        acknowledgeColor={values?.setpoint.acknowledgeColor ?? ''} 
                        openSideSheet={openSideSheet} 
                        onCloseSideSheet={() => setOpenSideSheet(false)}
                        viewOptionState={viewOptionState}
                        setViewOptionState={setViewOptionState} 
                        visible={state.activeTab === AlarmSetpointTestDisableTabEnum.TankBatteries ? true : false}
                        onDisabled={onDisabled}
                        />
                </TabContainer>
            </Box> 
        </Form>

    </FullScreenDialog> : <></>);
}

export type AlarmSetpointTestOrDisableDialogState = DialogState<AlarmSetpointTestOrDisableDialogInitValues, AlarmSetpointTestOrDisableDialogResult>;
export type BindAlarmSetpointTestOrDisableDialogReturn = BindDialogReturn<AlarmSetpointTestOrDisableDialogInitValues, AlarmSetpointTestOrDisableDialogResult>;

export function useAlarmSetpointTestOrDisableDialogState(): AlarmSetpointTestOrDisableDialogState {
    return useDialogState();
}

export function bindAlarmSetpointTestOrDisableDialog(props: AlarmSetpointTestOrDisableDialogState): BindAlarmSetpointTestOrDisableDialogReturn {
    return bindDialog(props);
}
